import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import PageHeader from '../../shell/PageHeader';
import TrendChart from '../../ui/TrendChart';
import { Alert, Button, Card, DataTable, TableCard } from '../../ui';
import { useAuth } from '../../../AuthContext';
import { Permissions } from '../../../utils/permissions';
import { formatCount, formatNaira } from '../../../utils/money';
import { apiErrorMessage } from '../../../utils/apiError';
import useTapDashboard, { TAP_CHART_METRICS } from './dashboard/useTapDashboard';
import tapTransactionColumns from './dashboard/tapTransactionColumns';
import { PeriodCaption, PeriodControls } from './dashboard/PeriodControls';
import SummaryGrid from './dashboard/SummaryGrid';
import BalanceCard from './dashboard/BalanceCard';
import TodayPanel from './dashboard/TodayPanel';
import useOwnPans from './sub-merchant/useOwnPans';
import PanSelect from './sub-merchant/PanSelect';
import SubMerchantDeactivated, { isSubMerchantDeactivated } from './sub-merchant/SubMerchantDeactivated';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Dashboard' }];

const SubMerchantDashboard = () => {
  const { hasPermission } = useAuth();
  const { params, updateParams, metric, setMetric, refetch, data, series, loading, error, requestError, paramError } =
    useTapDashboard();
  const pans = useOwnPans();
  const multiplePans = pans.length > 1;

  if (isSubMerchantDeactivated(requestError)) {
    return (
      <>
        <PageHeader title="Dashboard" breadcrumbs={breadcrumbs} />
        <SubMerchantDeactivated />
      </>
    );
  }

  const metricSelect = (
    <select
      className="form-select form-select-sm w-auto"
      aria-label="Chart metric"
      value={metric}
      onChange={(event) => setMetric(event.target.value)}
    >
      {TAP_CHART_METRICS.map((option) => (
        <option key={option.key} value={option.key}>
          {option.label}
        </option>
      ))}
    </select>
  );

  const todayEntries = [
    { label: 'Gross volume', value: formatNaira(data?.today?.gross_volume) },
    { label: 'Transactions', value: formatCount(data?.today?.txn_count) },
  ];

  const rejected = requestError?.response?.status === 422;

  return (
    <>
      <PageHeader
        title="Dashboard"
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {multiplePans && <PanSelect pans={pans} value={params.pan} onChange={(pan) => updateParams({ pan })} />}
            <PeriodControls params={params} onChange={updateParams} />
          </div>
        }
      />

      {paramError && (
        <Alert variant="warning" className="mb-6">
          {paramError}
        </Alert>
      )}

      {rejected ? (
        <Alert variant="warning">{apiErrorMessage(requestError, error)}</Alert>
      ) : error ? (
        <Alert variant="danger">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <Button variant="outline-danger" size="sm" onClick={refetch}>
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Retry
            </Button>
          </div>
        </Alert>
      ) : (
        <>
          <PeriodCaption period={data?.period} />

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <SummaryGrid metrics={data?.summary} loading={loading} />
            </div>

            <div className="col-span-12 xl:col-span-9">
              <Card title={series?.label ?? 'Trend'} className="mb-0" actions={metricSelect}>
                <TrendChart series={series} loading={loading} />
              </Card>
            </div>

            <div className="col-span-12 flex flex-col gap-6 xl:col-span-3">
              <BalanceCard
                title="Ledger balance"
                caption="Across all PANs"
                balance={data?.balance?.ledger_balance}
                loading={loading}
                ledgerTo={hasPermission(Permissions.subMerchantLedger) ? '/layout/sub-merchant-ledger' : null}
              />
              <TodayPanel today={data?.today} entries={todayEntries} loading={loading} className="flex-1" />
            </div>

            <div className="col-span-12">
              <TableCard
                title="Recent transactions"
                className="mb-0"
                toolbar={
                  hasPermission(Permissions.subMerchantTransactions) && (
                    <Link to="/layout/sub-merchant-transactions" className="btn btn-outline-primary btn-sm">
                      View all
                    </Link>
                  )
                }
              >
                <DataTable
                  columns={tapTransactionColumns({ showPan: multiplePans })}
                  rows={data?.recent_transactions ?? []}
                  rowKey={(row) => row.id}
                  serial
                  loading={loading}
                  empty={{ title: 'No transactions yet', message: 'No one has paid you with CashTap yet.' }}
                />
              </TableCard>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SubMerchantDashboard;
